import api from './client';

export type WorkflowSummary = {
  id: string;
  key: string;
  name: string;
  status: string;
  approved_root_concept_ids: string[];
  approved_taxonomy_revision: string | null;
  proposal_id: string | null;
  /** The customer taxonomy has changed under the approved snapshot. */
  taxonomy_outdated: boolean;
};

export type TaxonomyChange = {
  kind: 'superseded' | 'removed' | 'renamed' | 'moved' | 'added';
  concept_id: string;
  replaced_by: string | null;
  old_name: string | null;
  new_name: string | null;
  grounding_entry_count: number;
};

export type GroundingSection = 'definition' | 'keyword' | 'example';
export type GroundingPolarity = 'positive' | 'negative';
export type GroundingModality = 'text' | 'image' | 'video';

export type GroundingFields = {
  section: GroundingSection;
  polarity: GroundingPolarity;
  modalities: GroundingModality[];
  content: string;
};

export type ApprovedWorkflow = {
  workflow: WorkflowSummary;
  routing_definition: string;
  routing_keywords: string;
  enabled: boolean;
  include_non_violative: boolean;
  nodes: Array<{concept_id: string; parent_concept_id: string | null; name: string; path: string}>;
  grounding: Array<{id: string; concept_id: string} & GroundingFields>;
  taxonomy_changes: TaxonomyChange[];
  /** The approved roots carried through superseded concepts. */
  current_root_concept_ids: string[];
};

export type GroundingPreview = {
  entries: Array<{
    entry_id: string | null;
    concept_id: string;
    path: string;
    section: GroundingSection;
    polarity: GroundingPolarity;
    modalities: GroundingModality[];
    content: string;
    source: string;
  }>;
  errors: string[];
  added_count: number;
  changed_count: number;
  retired_count: number;
};

export async function listWorkflows(): Promise<WorkflowSummary[]> {
  const response = await api.get<WorkflowSummary[]>('/rd/workflows');
  return response.data;
}

export type CreateWorkflowRequest = {name: string; key: string};

export type CustomerTaxonomy = {
  pack_version: string | null;
  nodes: Array<{
    concept_id: string;
    parent_concept_id: string | null;
    name: string;
    display_name: string;
  }>;
};

export async function getCustomerTaxonomy(): Promise<CustomerTaxonomy> {
  const response = await api.get<CustomerTaxonomy>('/rd/workflows/taxonomy');
  return response.data;
}

export async function createWorkflow(request: CreateWorkflowRequest): Promise<WorkflowSummary> {
  const response = await api.post<WorkflowSummary>('/rd/workflows', request);
  return response.data;
}

export async function deleteWorkflow(id: string): Promise<void> {
  await api.delete(`/rd/workflows/${encodeURIComponent(id)}`);
}

export async function getWorkflow(id: string): Promise<ApprovedWorkflow> {
  const response = await api.get<ApprovedWorkflow>(`/rd/workflows/${encodeURIComponent(id)}`);
  return response.data;
}

export type GroundingProposal = {
  id: string;
  status: string;
  author: string;
  base_revision: string;
  submitted_at: string;
  replacement: {
    entries: GroundingPreview['entries'];
    previous: Array<{id: string; concept_id: string} & GroundingFields>;
  };
};

export type WorkflowConfigurationRequest = {
  base_revision: string | null;
  pack_version: string;
  root_concept_ids: string[];
  name: string;
  routing_definition: string;
  routing_keywords: string;
  enabled: boolean;
  include_non_violative: boolean;
};

export type WorkflowProposal = {
  id: string;
  status: string;
  author: string;
  base_revision: string;
  configuration: WorkflowConfigurationRequest;
};

export type ArchiveProposal = {id: string; status: string; author: string; base_revision: string};

export async function getWorkflowProposalKind(
  id: string,
  proposalId: string
): Promise<{kind: string; author: string}> {
  const response = await api.get<{kind: string; author: string}>(
    `/rd/workflows/${encodeURIComponent(id)}/proposals/${encodeURIComponent(proposalId)}/kind`
  );
  return response.data;
}

export async function submitArchiveProposal(id: string): Promise<ArchiveProposal> {
  const response = await api.post<ArchiveProposal>(
    `/rd/workflows/${encodeURIComponent(id)}/proposals/archive`
  );
  return response.data;
}

export async function submitWorkflowProposal(
  id: string,
  configuration: WorkflowConfigurationRequest
): Promise<WorkflowProposal> {
  const response = await api.post<WorkflowProposal>(
    `/rd/workflows/${encodeURIComponent(id)}/proposals`,
    configuration
  );
  return response.data;
}

export async function getWorkflowProposal(
  id: string,
  proposalId: string
): Promise<WorkflowProposal> {
  const response = await api.get<WorkflowProposal>(
    `/rd/workflows/${encodeURIComponent(id)}/proposals/${encodeURIComponent(proposalId)}`
  );
  return response.data;
}

export async function approveWorkflowProposal(id: string, proposalId: string): Promise<void> {
  await api.post(
    `/rd/workflows/${encodeURIComponent(id)}/proposals/${encodeURIComponent(proposalId)}/approve`
  );
}

export async function rejectWorkflowProposal(id: string, proposalId: string): Promise<void> {
  await api.post(
    `/rd/workflows/${encodeURIComponent(id)}/proposals/${encodeURIComponent(proposalId)}/reject`
  );
}

export async function getGroundingProposal(
  id: string,
  proposalId: string
): Promise<GroundingProposal> {
  const response = await api.get<GroundingProposal>(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/${encodeURIComponent(proposalId)}`
  );
  return response.data;
}

export async function approveGrounding(id: string, proposalId: string): Promise<void> {
  await api.post(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/${encodeURIComponent(proposalId)}/approve`
  );
}

export async function rejectGrounding(id: string, proposalId: string): Promise<void> {
  await api.post(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/${encodeURIComponent(proposalId)}/reject`
  );
}

export async function previewGrounding(id: string, csv: string): Promise<GroundingPreview> {
  const response = await api.post<GroundingPreview>(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/preview`,
    {csv}
  );
  return response.data;
}

export async function submitGrounding(
  id: string,
  csv: string,
  confirmDestructive: boolean
): Promise<{id: string}> {
  const response = await api.post<{id: string}>(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/submit`,
    {csv, confirm_destructive: confirmDestructive}
  );
  return response.data;
}

export type GroundingEntryInput = {entry_id: string | null; concept_id: string} & GroundingFields;

export type GroundingRevision = {
  id: string;
  number: number;
  created_at: string;
  created_by: string;
  source: string;
  entry_count: number;
  active: boolean;
};

export async function previewGroundingEntries(
  id: string,
  entries: GroundingEntryInput[]
): Promise<GroundingPreview> {
  const response = await api.post<GroundingPreview>(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/preview`,
    {entries}
  );
  return response.data;
}

export async function submitGroundingEntries(
  id: string,
  entries: GroundingEntryInput[],
  confirmDestructive: boolean
): Promise<{id: string}> {
  const response = await api.post<{id: string}>(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/import/submit`,
    {entries, confirm_destructive: confirmDestructive}
  );
  return response.data;
}

export async function listGroundingRevisions(id: string): Promise<GroundingRevision[]> {
  const response = await api.get<GroundingRevision[]>(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/revisions`
  );
  return response.data;
}

export async function activateGroundingRevision(id: string, revisionId: string): Promise<void> {
  await api.post(
    `/rd/workflows/${encodeURIComponent(id)}/grounding/revisions/${encodeURIComponent(revisionId)}/activate`
  );
}
